import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Member {
  id: string
  name: string
  email: string
  tier: string
  current_points: number
  lifetime_points: number
  points_to_next_tier: number
  rewards_redeemed: number
  total_orders: number
  total_spent: number
  joined_at: string
}

interface Tier {
  id: string
  name: string
  min_points: number
  points_multiplier: number
  benefits: string[]
  members_count: number
  color: string
}

interface Reward {
  id: string
  name: string
  type: string
  points_required: number
  description: string
  status: string
  redemptions_month: number
}

export default function LoyaltyProgramPage() {
  const [members, setMembers] = useState<Member[]>([])
  const [tiers, setTiers] = useState<Tier[]>([])
  const [rewards, setRewards] = useState<Reward[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [leaderboard, setLeaderboard] = useState<any[]>([])

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [membersRes, tiersRes, rewardsRes, statsRes, leaderRes] = await Promise.all([
      fetch(API_BASE + '/loyalty/members'),
      fetch(API_BASE + '/loyalty/tiers'),
      fetch(API_BASE + '/loyalty/rewards'),
      fetch(API_BASE + '/loyalty/statistics'),
      fetch(API_BASE + '/loyalty/leaderboard')
    ])
    if (membersRes.ok) setMembers((await membersRes.json()).members || [])
    if (tiersRes.ok) setTiers((await tiersRes.json()).tiers || [])
    if (rewardsRes.ok) setRewards((await rewardsRes.json()).rewards || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
    if (leaderRes.ok) setLeaderboard((await leaderRes.json()).leaderboard || [])
  }

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'platinum': return 'bg-gray-200 text-gray-800'
      case 'gold': return 'bg-yellow-100 text-yellow-800'
      case 'silver': return 'bg-gray-100 text-gray-600'
      case 'bronze': return 'bg-orange-100 text-orange-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Loyalty Program</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Add Member</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Members</div>
              <div className="text-2xl font-bold">{statistics.total_members?.toLocaleString()}</div>
              <div className="text-xs text-green-600">{statistics.active_members_30d?.toLocaleString()} active</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Points in Circulation</div>
              <div className="text-2xl font-bold text-purple-600">{(statistics.total_points_circulation / 1000).toFixed(0)}K</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Points Earned (Month)</div>
              <div className="text-2xl font-bold text-green-600">{(statistics.points_earned_month / 1000).toFixed(0)}K</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Points Redeemed</div>
              <div className="text-2xl font-bold text-blue-600">{(statistics.points_redeemed_month / 1000).toFixed(0)}K</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Redemption Rate</div>
              <div className="text-2xl font-bold">{statistics.redemption_rate}%</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-4 gap-4 mb-8">
          {tiers.map(tier => (
            <div key={tier.id} className="bg-white rounded-lg shadow p-4" style={{ borderTop: `4px solid ${tier.color}` }}>
              <div className="flex justify-between items-center mb-2">
                <span className="font-semibold">{tier.name}</span>
                <span className="text-sm text-gray-500">{tier.points_multiplier}x points</span>
              </div>
              <div className="text-3xl font-bold mb-2">{tier.members_count.toLocaleString()}</div>
              <div className="text-sm text-gray-500 mb-2">members</div>
              <div className="text-xs text-gray-500">
                Min {tier.min_points.toLocaleString()} points
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Top Members</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Member</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Points</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Spent</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {members.slice(0, 10).map(member => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{member.name}</div>
                      <div className="text-sm text-gray-500">{member.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getTierColor(member.tier)}`}>
                        {member.tier}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-bold text-purple-600">{member.current_points.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">{member.points_to_next_tier.toLocaleString()} to next tier</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium">{formatCurrency(member.total_spent)}</div>
                      <div className="text-xs text-gray-500">{member.total_orders} orders</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Leaderboard</h3>
              <div className="space-y-3">
                {leaderboard.slice(0, 5).map((member, i) => (
                  <div key={member.member_id} className="flex items-center gap-3 p-2 bg-gray-50 rounded">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${i === 0 ? 'bg-yellow-400 text-yellow-900' : i === 1 ? 'bg-gray-300 text-gray-700' : i === 2 ? 'bg-orange-300 text-orange-900' : 'bg-gray-100 text-gray-600'}`}>
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm">{member.name}</div>
                      <div className={`text-xs ${getTierColor(member.tier)} px-1 rounded inline-block`}>{member.tier}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-purple-600">{member.points.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">points</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Available Rewards</h3>
              <div className="space-y-3">
                {rewards.slice(0, 6).map(reward => (
                  <div key={reward.id} className="p-3 bg-gray-50 rounded">
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-medium text-sm">{reward.name}</span>
                      <span className="text-sm font-bold text-purple-600">{reward.points_required.toLocaleString()} pts</span>
                    </div>
                    <div className="text-xs text-gray-500 mb-1">{reward.description}</div>
                    <div className="text-xs text-green-600">{reward.redemptions_month} redeemed this month</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
